import { create } from 'zustand';
import { CSRF_COOKIE_NAME } from '@/lib/api/client';

/**
 * Auth state. `isAuthenticated` is hydrated synchronously at boot from the
 * JS-readable csrf_token cookie (the session itself is HttpOnly), so the shell
 * can paint before who-am-i returns.
 *
 *   idle      — signed out
 *   restoring — cookie present, who-am-i in flight
 *   ready     — profile loaded
 *   mfa       — signed in, but this device must be verified with an emailed code
 *   no-access — signed in, but the profile holds no SREG capability
 *   error     — who-am-i failed transiently; retryable
 */
export type SessionStatus = 'idle' | 'restoring' | 'ready' | 'mfa' | 'no-access' | 'error';

interface AuthState {
  isAuthenticated: boolean;
  sessionStatus: SessionStatus;
  setAuthenticated: (value: boolean) => void;
  setSessionStatus: (value: SessionStatus) => void;
}

function detectInitialAuth(): boolean {
  return typeof document !== 'undefined' && new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=`).test(document.cookie);
}

const initialAuth = detectInitialAuth();

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: initialAuth,
  sessionStatus: initialAuth ? 'restoring' : 'idle',
  setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setSessionStatus: (sessionStatus) => set({ sessionStatus }),
}));
